import React, { useEffect, useState } from 'react';
import { Block } from '@awarego-ui/components';
import { observer } from 'mobx-react';
import { useStores } from '../../../../hooks/use-stores';
import SubjectPreviewContainer from '../../../subjectPreview';
import RecommendationHeader from './RecommendationHeader';
import RecommendationSubheader from './RecommendationSubheader';
import RecommendationVideos from './RecommendationVideos';
import RecommendationTrainingGroups from './RecommendationTrainingGroups';
import NoItemsYetContent from '../../../../components/manager/no-items-yet-content';

function RecommendationContainer({ companyId }) {
    const { assessmentStore, catalogStore, store } = useStores();
    const [previewLocation, setPreviewLocation] = useState(null);
    const [previewSubjectModal, setPreviewSubjectModal] = useState(false);
    const [selectedListId, setSelectedListId] = useState('all');
    let [areas, setAreas] = useState(null);
    let [recommendations, setRecommendations] = useState(null);

    const { editingAssessment } = assessmentStore;
    useEffect(() => {
        doLoadData();
    }, []);

    useEffect(() => {
        refreshAreas();
        refreshRecommendations();
    }, [
        selectedListId,
        editingAssessment.areas,
        editingAssessment.recommendations,
    ]);

    const isCustom =
        selectedListId === 'all'
            ? editingAssessment.recommendations?.custom
            : editingAssessment._rawData.recommendationsByList?.[selectedListId]
                  ?.custom;

    const refreshAreas = () => {
        let sortedAreas;
        if (selectedListId == 'all') {
            sortedAreas = (editingAssessment.areas || [])
                .slice()
                .sort((a, b) => a.score - b.score);
        } else {
            if (editingAssessment._rawData.areasListReport) {
                const listsWithAreas =
                    editingAssessment._rawData.areasListReport;
                const list = listsWithAreas.find(
                    (list) => list.listId == selectedListId
                );
                sortedAreas = (list?.areas || [])
                    .slice()
                    .sort((a, b) => a.score - b.score);
            }
        }

        const recs =
            selectedListId === 'all'
                ? editingAssessment.recommendations
                : editingAssessment._rawData.recommendationsByList?.[
                      selectedListId
                  ];
        const finalAreaName = recs?.finalTestAreaName || 'Follow-up test';
        if (recs?.videos?.[finalAreaName]?.length) {
            sortedAreas = [
                ...(sortedAreas || []),
                { name: finalAreaName, score: null },
            ];
        }

        setAreas(sortedAreas);
    };

    const refreshRecommendations = () => {
        let videoRecommendations = [];
        if (selectedListId == 'all') {
            videoRecommendations =
                editingAssessment.recommendations?.videos || [];
        } else {
            if (editingAssessment._rawData.recommendationsByList) {
                const foundList =
                    editingAssessment._rawData.recommendationsByList[
                        selectedListId
                    ];
                if (foundList != undefined) {
                    videoRecommendations = foundList.videos || [];
                } else {
                    videoRecommendations = [];
                }
            }
        }
        setRecommendations(videoRecommendations);
    };

    const doLoadData = async () => {
        await catalogStore.loadCatalog(companyId);
        await catalogStore.loadThreatAreaCatalog(companyId);
    };

    const handleVideoClick = (subjectId) => {
        setPreviewLocation(subjectId);
        setPreviewSubjectModal(true);
    };

    const hidePreviewSubjectModal = () => {
        setPreviewSubjectModal(false);
    };

    const reloadVideos = (listId) => {
        setSelectedListId(listId);
    };

    const showCreateAsOneSection =
        !isCustom ||
        store.currentCompanyUISettings?.[
            'ui.assessment.recommendations.create_as_one'
        ] !== '0';

    const showMultiTraining =
        Boolean(isCustom) &&
        store.currentCompanyUISettings?.[
            'ui.assessment.recommendations.multi_training'
        ] !== '0';

    // When "Create as one" is off, always use all lists (no list filter).
    useEffect(() => {
        if (!showCreateAsOneSection && selectedListId !== 'all') {
            setSelectedListId('all');
        }
    }, [showCreateAsOneSection, selectedListId]);

    return (
        <>
            <Block>
                {editingAssessment &&
                ((editingAssessment.isScorm && recommendations && areas) ||
                    (!editingAssessment.isScorm &&
                        editingAssessment._rawData.areasListReport &&
                        recommendations &&
                        areas)) ? (
                    <>
                        {showCreateAsOneSection && (
                            <>
                                <RecommendationHeader
                                    listId={selectedListId}
                                    isCustom={Boolean(isCustom)}
                                />
                                <hr style={{ margin: '0px' }} />
                                <RecommendationSubheader
                                    videos={recommendations}
                                    lists={
                                        editingAssessment.isScorm
                                            ? null
                                            : editingAssessment._rawData
                                                  .areasListReport
                                    }
                                    reloadVideos={reloadVideos}
                                    isCustom={isCustom}
                                    showProgramSummary
                                    showListSelector
                                    scoreThreshold={
                                        selectedListId === 'all'
                                            ? editingAssessment.recommendations
                                                  ?.scoreThreshold
                                            : editingAssessment._rawData
                                                  .recommendationsByList?.[
                                                  selectedListId
                                              ]?.scoreThreshold
                                    }
                                    eligibleEmployeesCount={
                                        selectedListId === 'all'
                                            ? editingAssessment.recommendations
                                                  ?.eligibleEmployeesCount
                                            : editingAssessment._rawData
                                                  .recommendationsByList?.[
                                                  selectedListId
                                              ]?.eligibleEmployeesCount
                                    }
                                />
                                <RecommendationVideos
                                    handleVideoClick={handleVideoClick}
                                    sortedAreas={areas}
                                    recommendedVideos={recommendations}
                                />
                            </>
                        )}

                        {showMultiTraining && (
                            <RecommendationTrainingGroups
                                companyId={companyId}
                                assessmentId={editingAssessment.id}
                                listId={
                                    showCreateAsOneSection
                                        ? selectedListId
                                        : 'all'
                                }
                                onSubjectClick={handleVideoClick}
                            />
                        )}
                    </>
                ) : (
                    <NoItemsYetContent
                        img={'/static/img/icon-booklist.svg'}
                        imgAlt={'No recommendations available'}
                        title={'No recommendations available'}
                    />
                )}
            </Block>

            {previewSubjectModal && (
                <SubjectPreviewContainer
                    isOpen={previewSubjectModal}
                    onClose={hidePreviewSubjectModal}
                    subjectId={previewLocation}
                    hideFooter={true}
                />
            )}
        </>
    );
}

export default observer(RecommendationContainer);
